/**
 * Utility functions for building campaign API payloads
 */

import { buildVastAndWebPayload } from './creative';
import { buildCampaignPayload } from './creative';
import { isUrlCreative } from '@/shared/constants/creativeTypes';

/**
 * Sanitizes screen address data to ensure all required fields are present
 */
export const sanitizeScreenAddress = (screen: any) => {
  return {
    ...screen,
    address:
      typeof screen.address === 'string'
        ? screen.address
        : {
            ...screen.address,
            landmark:
              screen.address?.landmark || screen.touchpoint?.name || 'N/A',
            street: screen.address?.street || 'N/A',
            city: screen.address?.city || 'N/A',
            state: screen.address?.state || 'N/A',
            pincode: screen.address?.pincode || 'N/A',
            country: screen.address?.country || 'India',
          },
  };
};

/**
 * Checks if a campaign contains programmatic creatives (VAST/WEB)
 */
export const isProgrammaticCampaign = (selectedScreens: any[]): boolean => {
  return selectedScreens.some(
    (screen: any) =>
      Array.isArray(screen.slots) &&
      screen.slots.some(
        (slot: any) =>
          Array.isArray(slot.creatives) &&
          slot.creatives.some(
            (creative: any) => isUrlCreative(creative?.type)
          )
      )
  );
};

/**
 * Builds the final campaign payload for API submission
 */
export const buildFinalCampaignPayload = (
  campaignData: {
    name: string;
    campaignStartDate: string;
    campaignEndDate: string;
    campaignBudget: number;
    source: string;
    industry?: string;
    agency?: string;
    brandFolder?: { id: string; name?: string } | null;
    scheduling?: any;
    useFallback?: boolean;
  },
  selectedScreens: any[],
  partnerId: string
) => {
  if (!partnerId) {
    throw new Error('Partner ID is required for campaign creation');
  }

  if (!selectedScreens?.length) {
    throw new Error('At least one screen must be selected');
  }

  // Build screens and collect unique creatives
  const { screens } = buildVastAndWebPayload(
    selectedScreens,
    partnerId,
    campaignData.brandFolder!.id,
    campaignData.scheduling,
    campaignData.useFallback,
    campaignData.brandFolder?.name
  );

  // Sanitize screen addresses
  const sanitizedScreens = screens.map(sanitizeScreenAddress);

  // Determine if this is a programmatic campaign
  const programmatic = isProgrammaticCampaign(selectedScreens);

  // Build campaign payload
  const campaign = buildCampaignPayload(
    {
      name: campaignData.name,
      startDate: campaignData.campaignStartDate,
      endDate: campaignData.campaignEndDate,
      source: campaignData.source || 'SCREEN_OWNER',
      costPerUnit: {
        currencyCode: 'INR',
        microAmount: campaignData.campaignBudget,
      },
      ...(campaignData.industry && { industry: campaignData.industry }),
      ...(campaignData.agency && { agency: campaignData.agency }),
      ...(campaignData.brandFolder?.id && {
        brandCreative: campaignData.brandFolder.id,
      }),
    },
    programmatic
  );

  return {
    campaign,
    screens: sanitizedScreens,
  };
};
